import classNames from "classnames"
import { useEffect, useState } from "react"
import { Button } from "../../../../../components/button/Button"
import { CheckBox, Input, TextArea } from "../../../../../components/form/input"
import {
  deleteTask,
  finishOperation,
  updateTask,
} from "../../../../../fetch/tasks"
import styles from "./finish-task.module.css"
import { formatDate } from "../../../../../lib/formatDate"
import { toast } from "react-toastify"
import { Task, TaskStatus, UserRole } from "../../../../../../types"
import { tenantState } from "../../../../../store/tenant"
import { useWatcher } from "../../../../../lib/state/useWatcher"
import { ConfirmDelete } from "../../../../../components/confirm/ConfirmDelete"
import { BreadCrumb, BreadCrumbs } from "@/components/breadcrumbs/BreadCrumbs"
import { usersApi } from "@/fetch/api/users"
import { isBitus } from "src/config/urls"
import { Combobox } from "@/components/combobox/Combobox"
import { userState } from "@/store/user"
import { hasUserAccess } from "@/utils/user"
import { Dates } from "@/pages/main/user/Dates"

export const FinishTask = ({
  opId,
  isOpen,
  handleClose,
  editTask,
  breadcrumbs,
  districtId,
}: {
  isOpen: boolean
  opId?: number
  handleClose: (reload?: boolean) => void
  isFinishing?: boolean
  editTask?: Task
  breadcrumbs: BreadCrumb[]
  districtId?: number
}) => {
  const user = useWatcher(userState)
  const canAssignWorkerId = hasUserAccess({
    neededRole: UserRole.Manager,
    userRole: user.role,
  })
  const {
    settings: { features },
  } = useWatcher(tenantState)

  const [date, setDate] = useState(formatDate(new Date()))
  const [minutes, setMinutes] = useState(0)
  const [comment, setComment] = useState("")
  const [hours, setHours] = useState(0)
  const [units, setUnits] = useState("0")
  const [isDefect, setIsDefect] = useState(false)
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [isMoreWorkers, setMoreWorkers] = useState(false)
  const [workers, setWorkers] = useState<
    { id: number; name: string; role: UserRole }[]
  >([])
  const [selectedWorkers, setSelectedWorkers] = useState<number[]>([])
  const [overrideWorkerId, setOverrideWorkerId] = useState<number | undefined>(
    canAssignWorkerId ? user.id : undefined,
  )

  useEffect(() => {
    const minutes = (editTask?.timeUsed ?? 0) % 60
    const hours = Math.floor((editTask?.timeUsed ?? 0) / 60)

    setMinutes(minutes)
    setHours(hours)
    setUnits(`${editTask?.unitsUsed ?? 0}`)
    setComment(editTask?.comment ?? "")
    setIsDefect(editTask?.defect ?? false)

    usersApi
      .getAvailableWorkers(districtId ? `districtId=${districtId}` : "")
      .then((users) => {
        setWorkers(users)
      })
  }, [isOpen, editTask, districtId])

  const handleSubmit = async () => {
    if (!opId) {
      return
    }
    const body: any = {
      date: formatDate(new Date(date)),
      timeUsed: hours * 60 + minutes,
      operationId: opId,
      // unitsUsed: units,
      comment,
      defect: isDefect,
    }
    if (features["units"]) {
      body.unitsUsed = +units || 0
    }

    if (isMoreWorkers && selectedWorkers.length) {
      body.extraWorkerIds = selectedWorkers
    }

    if (canAssignWorkerId && overrideWorkerId && overrideWorkerId !== user.id) {
      body.overrideWorkerId = overrideWorkerId
    }

    const hasUnitsOrTime = +units || hours || minutes

    const nextErrors: Record<string, boolean> = {}
    if (!date) {
      nextErrors.date = true
      setErrors(nextErrors)
      return
    }

    if (!hasUnitsOrTime || (!hours && !minutes)) {
      if (features["units"]) {
        nextErrors.units = true
        setErrors(nextErrors)
      }
      toast(
        features["units"] ? "Jānorāda vienības vai laiks" : "Jānorāda laiks",
        { type: "error" },
      )
      return
    }

    if (!comment.trim() && isDefect) {
      nextErrors.comment = true
      setErrors(nextErrors)
      return
    }

    try {
      if (editTask) {
        const re = await updateTask(editTask.id, body)
        if (re && re.ok) {
          toast("Darbs veiksmīgi izlabots", { type: "success" })
          handleClose(true)
          setHours(0)
          setMinutes(0)
          setUnits("0")
        } else {
          throw new Error("")
        }

        return
      }
      const re = await finishOperation(body)
      if (re && re.ok) {
        toast("Darbs veiksmīgi pievienots", { type: "success" })
        handleClose(true)
        setHours(0)
        setMinutes(0)
        setUnits("0")
      } else {
        throw new Error("")
      }
    } catch (err) {
      toast("Neizdevās pievienot uzdevumu", { type: "error" })
      console.log(err)
    }
  }

  const onDateChange = (value: string) => {
    setDate(value)
  }

  const onCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setErrors((curr) => ({ ...curr, comment: false }))
    setComment(e.target.value)
  }
  const onDefectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDefect(e.target.checked)
  }
  const onMoreWorkersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMoreWorkers(e.target.checked)
  }
  const onHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = +e.target.value
    if (isNaN(next)) {
      return
    }
    setErrors((curr) => ({ ...curr, units: false }))
    setHours(next)
  }
  const onMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = +e.target.value
    if (isNaN(next)) {
      return
    }
    setErrors((curr) => ({ ...curr, units: false }))
    setMinutes(next)
  }

  const onUnitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    // if (isNaN(next)) {
    //   return
    // }
    setErrors((curr) => ({ ...curr, units: false }))
    setUnits(next)
  }

  const handleDeleteTask = async (comment?: string) => {
    if (editTask != null) {
      await deleteTask(editTask.id, comment)
      handleClose(true)
    }
  }

  const now = new Date().getHours()
  const isAfterMidnight = now > 0 && now < 4

  return (
    <div className={classNames(styles.bottomSheet, isOpen ? styles.open : "")}>
      <div className={styles.inner}>
        {breadcrumbs ? <BreadCrumbs items={breadcrumbs} /> : null}
        <div className={styles.title}>
          {editTask ? "Labot uzdevumu" : "Pievienot laiku"}
        </div>
        {isAfterMidnight ? (
          <div className={styles.blockQuote}>
            Pārliecinieties par pareiza datuma izvēli
          </div>
        ) : null}
        <Dates onSelect={onDateChange} selected={date} width={7} maxDate={formatDate(new Date())} />
        <div className={styles.inputs}>
          <Input
            type="date"
            label="Datums"
            min="2024-07-07"
            max={formatDate(new Date())}
            onChange={(e) => onDateChange(e.target.value)}
            value={date}
          />
        </div>
        {!editTask && canAssignWorkerId ? (
          <>
            <div className={styles.blockQuote}>
              Administrators var pievienot darba uzdevumu citam darbiniekam. Ja
              cits darbinieks nav izvēlēts, tad uzdevums tiks pievienots jums.
            </div>
            <div>Darbinieks kas veic uzdevumu:</div>
            <div className={styles.inputs}>
              <Combobox
                single={true}
                options={workers.filter((w) =>
                  hasUserAccess({
                    neededRole: w.role,
                    userRole: user.role,
                  }),
                )}
                onChange={(value) => setOverrideWorkerId(value[0])}
                selected={overrideWorkerId ? [overrideWorkerId] : undefined}
              />
            </div>
          </>
        ) : null}
        <div className={styles.inputs}>
          <Input
            label="Stundas"
            value={hours}
            error={errors.units && !hours}
            onChange={onHoursChange}
          />
          <Input
            label="Minūtes"
            value={minutes}
            error={errors.units && !minutes}
            onChange={onMinutesChange}
          />
        </div>
        {features["units"] ? (
          <div className={styles.inputs}>
            <Input
              label="Vienības"
              value={units}
              error={errors.units && !units}
              onChange={onUnitsChange}
            />
          </div>
        ) : null}
        {isBitus ? (
          <>
            <div className={styles.inputs}>
              <CheckBox
                label="Pievienot papildus darbiniekus"
                type="checkbox"
                checked={isMoreWorkers}
                onChange={onMoreWorkersChange}
              />
            </div>
            {isMoreWorkers ? (
              <>
                <div className={styles.blockQuote}>
                  Izvēlētajiem darbiniekiem tiks pievienots uzdevums ar tādu
                  pašu laiku, taču vienības tiks izdalītas ar darbinieku skaitu.
                </div>
                <div className={styles.inputs}>
                  <Combobox
                    options={workers}
                    onChange={setSelectedWorkers}
                    selected={selectedWorkers}
                  />
                </div>
              </>
            ) : null}
          </>
        ) : null}
        <div className={styles.inputs}>
          <TextArea
            label="Piezīmes"
            value={comment}
            error={errors.comment || (errors.units && !units)}
            onChange={onCommentChange}
          />
        </div>
        <div className={styles.inputs}>
          <CheckBox
            label="Atzīmēt kā pārtaisītu vienību"
            type="checkbox"
            checked={isDefect}
            onChange={onDefectChange}
          />
        </div>
        <div className={styles.buttonWrap}>
          <Button onClick={handleSubmit}>Saglabāt</Button>
          {editTask?.status === TaskStatus.Created ? (
            <ConfirmDelete
              Trigger={(props) => (
                <Button variant="secondary" {...props}>
                  Dzēst
                </Button>
              )}
              onConfirm={handleDeleteTask}
            />
          ) : null}
          <Button variant="secondary" onClick={() => handleClose()}>
            Atcelt
          </Button>
        </div>
      </div>
    </div>
  )
}
